import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthUser, useHasHydrated, useIsAuthenticated } from '../hooks/useAuth';
import { UserRole } from '../types/api';
import { FullPageSpinner } from '../components/ui/Spinner';
import { eventsService } from '../services/events.service';

const LOCK_PATH = '/organizer/cancellation-fees';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Liste de rôles autorisés. L'utilisateur passe si **au moins un** rôle
   * de sa liste `user.roles` est inclus dans `roles`.
   */
  roles?: UserRole[];
  /**
   * Si vrai, exige `kycStatus === 'APPROVED'` (ou rôle ADMIN).
   * Sinon redirige vers `/account/become-organizer`.
   */
  kycApproved?: boolean;
}

export const ProtectedRoute = ({ children, roles, kycApproved }: ProtectedRouteProps) => {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const user = useAuthUser();
  const hasHydrated = useHasHydrated();

  const userRoles: UserRole[] = user?.roles ?? [];
  const isAdmin = userRoles.includes('ADMIN');
  // Verrou compte organisateur (bug 14) : une créance d'annulation impayée
  // bloque toute route organisateur (pas les admins, jamais eux-mêmes en dette).
  const isOrganizerGate = !!roles?.includes('ORGANIZER');
  const needsCancellationFeeCheck = isOrganizerGate && isAuthenticated && !isAdmin;

  // Le hook doit TOUJOURS être appelé (règle des hooks) — `enabled` contrôle
  // si la requête part réellement.
  const { data: cancellationFees, isLoading: loadingFees } = useQuery({
    queryKey: ['cancellation-fees', 'mine'],
    queryFn: eventsService.myCancellationFees,
    enabled: needsCancellationFeeCheck,
    staleTime: 60_000,
  });

  // Tant que le store n'a pas relu le localStorage (juste après un rechargement
  // complet de page), on ne sait pas encore si l'utilisateur est authentifié —
  // ne JAMAIS décider d'un redirect sur cette base, sous peine d'éjecter un
  // utilisateur pourtant bien connecté (bug 1.3).
  if (!hasHydrated) {
    return <FullPageSpinner />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (roles && !roles.some((r) => userRoles.includes(r))) {
    return <Navigate to="/" replace />;
  }

  if (kycApproved && user.kycStatus !== 'APPROVED' && !isAdmin) {
    return <Navigate to="/account/become-organizer" replace />;
  }

  if (needsCancellationFeeCheck && location.pathname !== LOCK_PATH) {
    if (loadingFees) return <FullPageSpinner />;
    const hasUnpaid = (cancellationFees ?? []).some((f) => f.status === 'UNPAID');
    if (hasUnpaid) {
      return <Navigate to={LOCK_PATH} replace />;
    }
  }

  return <>{children}</>;
};
